import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  TextField,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import financialStatementsService, { formatCurrency, formatDate } from '../../services/financialStatementsService';

// TabPanel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`financial-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function FinancialStatements() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Date filters
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Statement data
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [trialBalance, setTrialBalance] = useState(null);

  useEffect(() => {
    loadData();
  }, [asOfDate, startDate, endDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [bsRes, isRes, cfRes, tbRes] = await Promise.all([
        financialStatementsService.getBalanceSheet({ as_of_date: asOfDate }),
        financialStatementsService.getIncomeStatement({ start_date: startDate, end_date: endDate }),
        financialStatementsService.getCashFlowStatement({ start_date: startDate, end_date: endDate }),
        financialStatementsService.getTrialBalance({ as_of_date: asOfDate })
      ]);

      if (bsRes.success) setBalanceSheet(bsRes);
      if (isRes.success) setIncomeStatement(isRes);
      if (cfRes.success) setCashFlow(cfRes);
      if (tbRes.success) setTrialBalance(tbRes);

    } catch (err) {
      console.error('Error loading financial statements:', err);
      setError('Failed to load financial statements. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Render Balance Sheet
  const renderBalanceSheet = () => {
    if (!balanceSheet?.data) return null;
    const { assets, liabilities, equity } = balanceSheet.data;
    const validation = balanceSheet.validation;
    const integrity = balanceSheet.integrity;
    const orphanCount = parseInt(integrity?.orphan_lines || 0, 10);
    const fxDroppedCount = parseInt(integrity?.fx_dropped_lines || 0, 10);

    return (
      <Box>
        {/* Validation Status */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          {validation?.is_balanced ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Balanced"
              color="success"
              size="small"
            />
          ) : (
            <Chip
              icon={<ErrorIcon />}
              label={`Variance: ${formatCurrency(validation?.variance)}`}
              color="error"
              size="small"
            />
          )}
          <Typography variant="body2" color="text.secondary">
            As of {formatDate(balanceSheet.as_of_date)}
          </Typography>
        </Box>

        {/* Integrity: name WHY a variance exists instead of leaving a bare red chip */}
        {(orphanCount > 0 || fxDroppedCount > 0) && (
          <Alert severity={orphanCount > 0 ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {orphanCount > 0 && (
              <span>
                <strong>{orphanCount} journal line(s)</strong> reference accounts missing from the
                chart of accounts (net {formatCurrency(integrity?.orphan_net_native)} native) and are
                excluded one-sided — a primary known source of the balance variance.{' '}
              </span>
            )}
            {fxDroppedCount > 0 && (
              <span>
                {fxDroppedCount} line(s) excluded for missing FX rates (net {formatCurrency(integrity?.fx_dropped_net_native)} native).
              </span>
            )}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Assets */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Assets
                </Typography>

                {/* Current Assets */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Typography>Current Assets</Typography>
                      <Typography fontWeight="bold">
                        {formatCurrency(assets.total_current_assets)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          {assets.current_assets?.map((account, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Typography variant="body2">{account.account_code}</Typography>
                              </TableCell>
                              <TableCell>{account.account_name}</TableCell>
                              <TableCell align="right">{formatCurrency(account.balance)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>

                {/* Non-Current Assets */}
                {assets.non_current_assets?.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                        <Typography>Non-Current Assets</Typography>
                        <Typography fontWeight="bold">
                          {formatCurrency(assets.total_non_current_assets)}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            {assets.non_current_assets.map((account, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Typography variant="body2">{account.account_code}</Typography>
                                </TableCell>
                                <TableCell>{account.account_name}</TableCell>
                                <TableCell align="right">{formatCurrency(account.balance)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}

                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="white">
                      Total Assets
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="white">
                      {formatCurrency(assets.total_assets)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Liabilities & Equity */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Liabilities & Equity
                </Typography>

                {/* Current Liabilities */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Typography>Current Liabilities</Typography>
                      <Typography fontWeight="bold">
                        {formatCurrency(liabilities.total_current_liabilities)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          {liabilities.current_liabilities?.map((account, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Typography variant="body2">{account.account_code}</Typography>
                              </TableCell>
                              <TableCell>{account.account_name}</TableCell>
                              <TableCell align="right">{formatCurrency(account.balance)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>

                {/* Equity */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Typography>Equity</Typography>
                      <Typography fontWeight="bold">
                        {formatCurrency(equity.total_equity)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          {equity.items?.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Typography variant="body2">{item.account_code}</Typography>
                              </TableCell>
                              <TableCell>{item.account_name}</TableCell>
                              <TableCell align="right">{formatCurrency(item.balance)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>

                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="white">
                      Total Liabilities & Equity
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="white">
                      {formatCurrency(validation?.total_liabilities_and_equity)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render Income Statement
  const renderIncomeStatement = () => {
    if (!incomeStatement?.data) return null;
    const data = incomeStatement.data;
    const revenue = data.revenue;
    const cogs = data.cost_of_goods_sold;
    const expenses = data.operating_expenses;

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Period: {formatDate(startDate)} to {formatDate(endDate)}
        </Typography>

        <Card>
          <CardContent>
            {/* Revenue Section */}
            <Typography variant="h6" color="success.main" gutterBottom>
              Revenue
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableBody>
                  {revenue?.items?.map((account, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{account.account_code}</TableCell>
                      <TableCell>{account.account_name}</TableCell>
                      <TableCell align="right">{formatCurrency(account.amount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'success.light' }}>
                    <TableCell colSpan={2}><strong>Total Revenue</strong></TableCell>
                    <TableCell align="right"><strong>{formatCurrency(revenue?.total_revenue)}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* COGS Section */}
            {cogs?.items?.length > 0 && (
              <>
                <Typography variant="h6" color="warning.main" gutterBottom>
                  Cost of Goods Sold
                </Typography>
                <TableContainer sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableBody>
                      {cogs.items.map((account, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{account.account_code}</TableCell>
                          <TableCell>{account.account_name}</TableCell>
                          <TableCell align="right">{formatCurrency(account.amount)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'warning.light' }}>
                        <TableCell colSpan={2}><strong>Total COGS</strong></TableCell>
                        <TableCell align="right"><strong>{formatCurrency(cogs.total_cogs)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ p: 2, mb: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Gross Profit</Typography>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {formatCurrency(data.gross_profit)}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {/* Expenses Section */}
            <Typography variant="h6" color="error.main" gutterBottom>
              Operating Expenses
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableBody>
                  {expenses?.items?.length > 0 ? (
                    expenses.items.map((account, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{account.account_code}</TableCell>
                        <TableCell>{account.account_name}</TableCell>
                        <TableCell align="right">{formatCurrency(account.amount)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        No operating expenses
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow sx={{ bgcolor: 'error.light' }}>
                    <TableCell colSpan={2}><strong>Total Expenses</strong></TableCell>
                    <TableCell align="right"><strong>{formatCurrency(expenses?.total_expenses)}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Net Income */}
            <Box sx={{ p: 2, bgcolor: 'primary.main', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" color="white">Net Income</Typography>
                <Typography variant="h6" color="white">
                  {formatCurrency(data.net_income)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Render Cash Flow Statement
  const renderCashFlow = () => {
    if (!cashFlow?.data) return null;
    const data = cashFlow.data;
    const operating = data.operating_activities;
    const investing = data.investing_activities;
    const financing = data.financing_activities;
    const validation = cashFlow.validation;

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Period: {formatDate(startDate)} to {formatDate(endDate)}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Beginning Cash</Typography>
                <Typography variant="h6">{formatCurrency(data.opening_cash_balance)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Net Change</Typography>
                <Typography
                  variant="h6"
                  color={parseFloat(data.net_change_in_cash) >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(data.net_change_in_cash)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Ending Cash</Typography>
                <Typography variant="h6">{formatCurrency(data.ending_cash_balance)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Reconciliation</Typography>
                {validation?.is_reconciled ? (
                  <Chip icon={<CheckCircleIcon />} label="Reconciled" color="success" size="small" />
                ) : (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`Diff: ${formatCurrency(validation?.variance)}`}
                    color="warning"
                    size="small"
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            {/* Operating Activities */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Operating Activities
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Net Income</TableCell>
                      <TableCell align="right">{formatCurrency(operating?.net_income)}</TableCell>
                    </TableRow>
                    {operating?.adjustments?.map((adj, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ pl: 4 }}>{adj.description || adj.account_name}</TableCell>
                        <TableCell align="right">{formatCurrency(adj.amount || adj.change)}</TableCell>
                      </TableRow>
                    ))}
                    {operating?.changes_in_working_capital?.map((item, idx) => (
                      <TableRow key={`wc-${idx}`}>
                        <TableCell sx={{ pl: 4 }}>{item.account_name}</TableCell>
                        <TableCell align="right">{formatCurrency(item.change)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Net Cash from Operating</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(operating?.total_operating)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Investing Activities */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Investing Activities
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {investing?.items?.length > 0 ? (
                      investing.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.description || item.account_name}</TableCell>
                          <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                          No investing activities
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Net Cash from Investing</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(investing?.total_investing)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Financing Activities */}
            <Box>
              <Typography variant="h6" color="primary" gutterBottom>
                Financing Activities
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {financing?.items?.length > 0 ? (
                      financing.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.description || item.account_name}</TableCell>
                          <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                          No financing activities
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Net Cash from Financing</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(financing?.total_financing)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Render Trial Balance
  const renderTrialBalance = () => {
    if (!trialBalance?.data) return null;
    const accounts = trialBalance.data.accounts;
    const totals = trialBalance.totals;
    const validation = trialBalance.validation;

    return (
      <Box>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          {(validation?.is_balanced || Math.abs(parseFloat(validation?.difference || 0)) < 0.01) ? (
            <Chip icon={<CheckCircleIcon />} label="Balanced" color="success" size="small" />
          ) : (
            <Chip icon={<ErrorIcon />} label={`Difference: ${formatCurrency(validation?.difference)}`} color="error" size="small" />
          )}
          <Typography variant="body2" color="text.secondary">
            As of {formatDate(trialBalance.as_of_date)}
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Account Code</TableCell>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Debits</TableCell>
                    <TableCell align="right">Credits</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts?.map((account, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{account.account_code}</TableCell>
                      <TableCell>{account.account_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={account.account_type}
                          size="small"
                          color={
                            account.account_type === 'asset' ? 'primary' :
                            account.account_type === 'liability' ? 'secondary' :
                            account.account_type === 'equity' ? 'info' :
                            account.account_type === 'revenue' ? 'success' :
                            'error'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(account.total_debits)}</TableCell>
                      <TableCell align="right">{formatCurrency(account.total_credits)}</TableCell>
                      <TableCell align="right">{formatCurrency(account.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.light' }}>
                    <TableCell colSpan={3}>
                      <Typography fontWeight="bold" color="white">TOTALS</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="white">{formatCurrency(totals?.total_debits)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="white">{formatCurrency(totals?.total_credits)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="white">-</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Financial Statements
        </Typography>
        <Tooltip title="Refresh Data">
          <IconButton onClick={loadData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Date Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="As of Date (Balance Sheet)"
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Start Date (P&L, Cash Flow)"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="End Date (P&L, Cash Flow)"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<AccountBalanceIcon />}
            label="Balance Sheet"
            iconPosition="start"
          />
          <Tab
            icon={<TrendingUpIcon />}
            label="Income Statement"
            iconPosition="start"
          />
          <Tab
            icon={<MonetizationOnIcon />}
            label="Cash Flow"
            iconPosition="start"
          />
          <Tab
            label="Trial Balance"
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {renderBalanceSheet()}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        {renderIncomeStatement()}
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        {renderCashFlow()}
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        {renderTrialBalance()}
      </TabPanel>
    </Box>
  );
}
